import {
  Body,
  Box,
  Checkbox,
  Dialog,
  Space,
  WarningIcon,
} from "@dbn/omnichannel-design-system-g4p";
import { boolean, option, taskEither } from "fp-ts";
import {
  RuntimeLocaleKey,
  useFormatMessage,
  useFormatRichMessage2,
} from "../intl";
import { useEffect, useState } from "react";
import { FranchiseBranchGdprData } from "./api";
import { pipe } from "fp-ts/function";
import { useCommand } from "../useAPI";
import * as gdprApi from "./api";
import {
  palette,
  spaceUnit,
} from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { useAppContext } from "../useAppContext";
import { TaskEither } from "fp-ts/TaskEither";

type Props = {
  onDismiss: () => void;
  onContinue: TaskEither<unknown, void>;
  paperBased?: boolean;
  onShareWithClient?: (gdprData: FranchiseBranchGdprData) => void;
  gdprData?: FranchiseBranchGdprData;
  accepted?: boolean;
};

export const GDPR = ({
  paperBased = false,
  onShareWithClient,
  accepted,
  ...props
}: Props) => {
  const getGdprData = useCommand(gdprApi.getFranchiseBranchGdprData);
  const setGdprConsent = useCommand(gdprApi.setFranchiseBranchGdprConsent);
  const formatMessage = useFormatMessage();
  const formatRichMessage = useFormatRichMessage2();
  const {
    apiParameters: { channel },
  } = useAppContext();
  const isInPersonChannel = channel.endsWith("InPerson");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [gdprData, setGdprData] = useState<FranchiseBranchGdprData | undefined>(
    props.gdprData
  );

  useEffect(() => {
    if (!props.gdprData) {
      pipe(
        getGdprData(),
        taskEither.chain(gdprData =>
          pipe(
            gdprData.franchiseBranchGdprRequired,
            boolean.fold(
              () =>
                taskEither.fromIO(() => {
                  props.onContinue();
                }),
              () =>
                taskEither.fromIO(() => {
                  setGdprData(gdprData);
                  onShareWithClient && onShareWithClient(gdprData);
                })
            )
          )
        )
      )();
    } else {
      onShareWithClient && onShareWithClient(props.gdprData);
    }
  }, []);

  useEffect(() => {
    if (accepted) {
      onContinue();
    }
  }, [accepted]);

  const onContinue = pipe(
    taskEither.fromIO(() => setIsSubmitting(true)),
    taskEither.chain(() => setGdprConsent({ accepted: true, paperBased })),
    taskEither.chain(() => props.onContinue),
    taskEither.mapLeft(() => setIsSubmitting(false))
  );

  const onReject = pipe(
    taskEither.fromIO(() => setIsSubmitting(true)),
    taskEither.chain(() => setGdprConsent({ accepted: false, paperBased })),
    taskEither.chain(() => taskEither.fromIO(props.onContinue))
  );

  const [iAgree, setIAgree] = useState(false);

  return (
    <>
      {gdprData?.franchiseBranchGdprRequired && (
        <Dialog
          ariaLabelMessageForCloseIcon={formatMessage(
            "Modal.closeButton" as RuntimeLocaleKey
          )}
          variant="document"
          onDismiss={option.some(props.onDismiss)}
          actions={
            onShareWithClient
              ? []
              : isInPersonChannel
              ? [
                  {
                    variant: "primary",
                    disabled: !iAgree,
                    label: formatMessage("Continue"),
                    action: onContinue,
                  },
                ]
              : [
                  {
                    variant: "secondary",
                    label: formatMessage("GDPR.remote.action.reject"),
                    action: onReject,
                  },
                  {
                    variant: "primary",
                    label: formatMessage("GDPR.remote.action.accept", {
                      name: gdprData?.franchiseBranchName,
                      city: gdprData?.franchiseBranchCity,
                    }),
                    action: onContinue,
                  },
                ]
          }
          isSubmitting={isSubmitting}
          maxBodyHeight={"50dvh"}
        >
          <Box
            column
            style={{
              margin: "6px",
              overflowY: "auto",
            }}
          >
            <Box column hAlignContent="center">
              <div
                style={{
                  height: "72px",
                  width: "72px",
                  padding: spaceUnit * 2,
                  display: "inline-block",
                  borderRadius: "50%",
                  border: palette.blue800,
                  backgroundColor: palette.blue800,
                }}
              >
                <WarningIcon size="x-large" color={palette.white} />
              </div>
              <Space units={4} />
              <Body size={"big"} weight={"medium"}>
                {formatMessage("GDPR.title")}
              </Body>
            </Box>
            <Space units={8} />
            <div>
              {formatRichMessage(
                isInPersonChannel
                  ? paperBased
                    ? "GDPR.paperBased.subtitle"
                    : "GDPR.subtitle"
                  : "GDPR.remote.subtitle",
                {
                  city: gdprData.franchiseBranchCity,
                },
                { useWhitespacePreWrap: true }
              )}
            </div>
            {isInPersonChannel && !onShareWithClient && (
              <Box style={{ paddingTop: "10px" }}>
                <Space units={4} />
                <Checkbox
                  name={"GDPR.checkbox"}
                  label={option.some(
                    paperBased
                      ? formatMessage("GDPR.paperBased.checkbox.label")
                      : formatMessage("GDPR.checkbox.label")
                  )}
                  onChange={() => setIAgree(prev => !prev)}
                  value={iAgree}
                  issuesType={option.none}
                />
              </Box>
            )}
            <Space units={4} />
          </Box>
        </Dialog>
      )}
    </>
  );
};
